import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  Share2, Copy, Wallet, Clock, TrendingUp, Award, Check, Users,
  QrCode, Download, Loader2, ArrowRight, PoundSterling,
} from "lucide-react";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
const pence = (p: number) => `£${(p / 100).toFixed(2)}`;
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

type Terms = { discountPercent: number; firstOrderPence: number; secondOrderPence: number; thirdOrderPence: number; subsequentOrderPence: number; clearanceDays: number; minWithdrawalPence: number };
type Me = { isInfluencer: boolean; status: "pending" | "active" | "paused" | "rejected" | null; displayName?: string; code?: string | null; shareUrl?: string | null; rejectionReason?: string | null; hasBankDetails?: boolean };
type Balance = { pendingPence: number; availablePence: number; reservedPence: number; paidPence: number; availableToWithdrawPence: number; lifetimePence: number; thisWeekPence: number };
type Dashboard = { code: string | null; shareUrl: string | null; balance: Balance; referredCount: number; repeatCustomerCount: number; commissionOrderCount: number };
type Earning = { id: number; referralRef: string; stage: string; amountPence: number; status: string; clearsAt: string | null; date: string };
type Withdrawal = { id: number; amountPence: number; feePence: number; status: string; adminNote: string | null; requestedAt: string; processedAt: string | null };

const DEFAULT_TERMS: Terms = { discountPercent: 20, firstOrderPence: 100, secondOrderPence: 30, thirdOrderPence: 20, subsequentOrderPence: 10, clearanceDays: 7, minWithdrawalPence: 1000 };

// ─── Earnings calculator ──────────────────────────────────────────────────────
function Calculator({ terms }: { terms: Terms }) {
  const presets = [50, 100, 250, 500, 1000];
  const [people, setPeople] = useState(100);
  const [expanded, setExpanded] = useState(false);
  const [newPerWeek, setNewPerWeek] = useState(25);
  const [weeks, setWeeks] = useState(8);

  const rate = (seq: number) => (seq === 1 ? terms.firstOrderPence : seq === 2 ? terms.secondOrderPence : seq === 3 ? terms.thirdOrderPence : terms.subsequentOrderPence);
  const wk = (n: number, seq: number) => n * rate(seq);

  // Expanded: each week a new cohort joins; every existing cohort places its next order.
  const expandedSeries = () => {
    const out: number[] = [];
    for (let w = 1; w <= weeks; w++) {
      let total = 0;
      for (let cohort = 1; cohort <= w; cohort++) {
        const seq = w - cohort + 1; // this cohort's order number this week
        total += wk(newPerWeek, seq);
      }
      out.push(total);
    }
    return out;
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><PoundSterling size={18} className="text-[#f0a500]" /> What could you earn?</h3>
      <p className="text-sm text-slate-500 mt-1">These are people who actually <strong>place an order</strong> — not followers, link clicks or code shares.</p>

      <div className="flex flex-wrap gap-2 mt-4">
        {presets.map((p) => (
          <button key={p} onClick={() => setPeople(p)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${people === p ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 border-slate-200"}`}>{p.toLocaleString()}</button>
        ))}
        <input type="number" min={1} value={people} onChange={(e) => setPeople(Math.max(0, Number(e.target.value) || 0))} className="w-24 px-3 py-1.5 rounded-full text-sm border border-slate-200" aria-label="Custom number of people" />
      </div>

      {!expanded && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[{ l: "Week 1", s: 1 }, { l: "Week 2", s: 2 }, { l: "Week 3", s: 3 }, { l: "Week 4+ (each)", s: 4 }].map((c) => (
            <div key={c.l} className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-center">
              <div className="text-xs text-slate-400">{c.l}</div>
              <div className="text-lg font-bold text-[#1e3a5f]">{pence(wk(people, c.s))}</div>
            </div>
          ))}
        </div>
      )}

      {expanded && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-3 mb-3">
            <label className="text-sm text-slate-600">New ordering customers / week
              <input type="number" min={0} value={newPerWeek} onChange={(e) => setNewPerWeek(Math.max(0, Number(e.target.value) || 0))} className="ml-2 w-20 px-2 py-1 rounded-lg border border-slate-200" /></label>
            <label className="text-sm text-slate-600">Weeks
              <input type="number" min={1} max={52} value={weeks} onChange={(e) => setWeeks(Math.min(52, Math.max(1, Number(e.target.value) || 1)))} className="ml-2 w-20 px-2 py-1 rounded-lg border border-slate-200" /></label>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {expandedSeries().map((v, i) => (
              <div key={i} className="min-w-[84px] rounded-xl bg-slate-50 border border-slate-100 p-2 text-center">
                <div className="text-[11px] text-slate-400">Week {i + 1}</div>
                <div className="text-sm font-bold text-[#1e3a5f]">{pence(v)}</div>
              </div>
            ))}
          </div>
          <div className="text-sm font-semibold text-slate-700 mt-2">Total over {weeks} weeks: {pence(expandedSeries().reduce((a, b) => a + b, 0))}</div>
        </div>
      )}

      <button onClick={() => setExpanded((v) => !v)} className="text-sm text-[#1e3a5f] font-semibold mt-4">{expanded ? "Show simple view" : "Show detailed calculator"}</button>
      <p className="text-xs text-slate-400 mt-3">Estimates assume everyone shown orders once per week. Actual earnings depend on completed orders. Referring new customers adds earnings on top of your existing customers’ repeat orders. This is not guaranteed income.</p>
    </div>
  );
}

// ─── Marketing + apply ────────────────────────────────────────────────────────
function OfferExplainer({ terms }: { terms: Terms }) {
  return (
    <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2a4a6f] text-white rounded-2xl p-6 shadow-sm">
      <h1 className="text-2xl font-bold flex items-center gap-2"><Share2 size={22} /> Ayrshire Hub Influencer Programme</h1>
      <p className="mt-3 text-white/90 leading-relaxed">
        Give new customers <strong>{terms.discountPercent}% off their first order</strong> at participating stores.
        Earn <strong>{pence(terms.firstOrderPence)}</strong> on their first order,{" "}
        <strong>{pence(terms.secondOrderPence)}</strong> on their second,{" "}
        <strong>{pence(terms.thirdOrderPence)}</strong> on their third and{" "}
        <strong>{pence(terms.subsequentOrderPence)}</strong> on every order after that — for life.
      </p>
      <p className="mt-2 text-sm text-white/70">Rewards depend on qualifying completed orders. Money becomes available to withdraw {terms.clearanceDays} days after an order is completed.</p>
    </div>
  );
}

/** Declined tracking view — shows status + reason; re-apply is a deliberate action. */
function DeclinedView({ reason, onApplied }: { reason?: string | null; onApplied: () => void }) {
  const [reapply, setReapply] = useState(false);
  return (
    <>
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-rose-800">Application declined</h2>
        <p className="text-rose-700 text-sm mt-1">{reason || "Your application wasn’t approved this time."}</p>
        {!reapply && (
          <button onClick={() => setReapply(true)} className="mt-4 text-sm font-semibold text-[#1e3a5f] underline">
            Apply again
          </button>
        )}
      </div>
      {reapply && <ApplyForm onApplied={onApplied} />}
    </>
  );
}

function ApplyForm({ onApplied }: { onApplied: () => void }) {
  const { toast } = useToast();
  const [displayName, setDisplayName] = useState("");
  const [socialHandle, setSocialHandle] = useState("");
  const [applicationNote, setApplicationNote] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!displayName.trim()) { toast({ title: "Please add your name", variant: "destructive" }); return; }
    setBusy(true);
    const res = await fetch("/api/influencer/apply", { method: "POST", headers: authHeaders(), body: JSON.stringify({ displayName, socialHandle, applicationNote }) });
    setBusy(false);
    if (res.ok) { toast({ title: "Application submitted 🎉", description: "We’ll review it shortly." }); onApplied(); }
    else { const e = await res.json().catch(() => ({})); toast({ title: e.error || "Could not submit", variant: "destructive" }); }
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <h3 className="text-lg font-bold text-slate-800">Apply to join</h3>
      <p className="text-sm text-slate-500 mt-1">Tell us a little about you. An admin will review and issue your unique code.</p>
      <div className="space-y-3 mt-4">
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
        <input value={socialHandle} onChange={(e) => setSocialHandle(e.target.value)} placeholder="Social handle (optional, e.g. @you)" className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
        <textarea value={applicationNote} onChange={(e) => setApplicationNote(e.target.value)} placeholder="How will you share Ayrshire Hub? (optional)" rows={3} className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
        <button onClick={submit} disabled={busy} className="w-full bg-[#f0a500] text-[#1e3a5f] font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />} Submit application
        </button>
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function StatCard({ label, value, Icon, accent }: { label: string; value: string; Icon: React.ElementType; accent?: boolean }) {
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${accent ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white border-slate-100"}`}>
      <div className={`flex items-center gap-1.5 text-xs ${accent ? "text-white/70" : "text-slate-400"}`}><Icon size={14} /> {label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent ? "text-white" : "text-[#1e3a5f]"}`}>{value}</div>
    </div>
  );
}

function Dashboard({ me, terms }: { me: Me; terms: Terms }) {
  const { toast } = useToast();
  const [data, setData] = useState<Dashboard | null>(null);
  const [earnings, setEarnings] = useState<Earning[] | null>(null);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[] | null>(null);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const load = useCallback(async () => {
    const [d, e, w] = await Promise.all([
      fetch("/api/influencer/dashboard", { headers: authHeaders() }).then((r) => r.ok ? r.json() : null),
      fetch("/api/influencer/earnings?limit=50", { headers: authHeaders() }).then((r) => r.ok ? r.json() : null),
      fetch("/api/influencer/withdrawals", { headers: authHeaders() }).then((r) => r.ok ? r.json() : null),
    ]);
    setData(d); setEarnings(e?.earnings ?? []); setWithdrawals(w?.withdrawals ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!data) return <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;
  const b = data.balance;
  const code = data.code ?? me.code ?? "";
  const shareUrl = data.shareUrl ?? me.shareUrl ?? "";

  async function copyCode() { await navigator.clipboard.writeText(code); setCopied(true); toast({ title: "Code copied" }); setTimeout(() => setCopied(false), 1800); }
  async function share() {
    const text = `Get ${terms.discountPercent}% off your first order at Ayrshire Hub with my code ${code}`;
    if (navigator.share) { try { await navigator.share({ title: "Ayrshire Hub", text, url: shareUrl }); } catch { /* cancelled */ } }
    else { await navigator.clipboard.writeText(`${text} ${shareUrl}`); toast({ title: "Share link copied" }); }
  }

  return (
    <div className="space-y-5">
      {me.status === "paused" && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3">Your code is paused for new sign-ups, but you keep earning on your existing customers’ orders.</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Available to withdraw" value={pence(b.availableToWithdrawPence)} Icon={Wallet} accent />
        <StatCard label="Pending" value={pence(b.pendingPence)} Icon={Clock} />
        <StatCard label="This week" value={pence(b.thisWeekPence)} Icon={TrendingUp} />
        <StatCard label="Lifetime" value={pence(b.lifetimePence)} Icon={Award} />
      </div>

      {/* Primary actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button onClick={share} className="bg-[#f0a500] text-[#1e3a5f] font-bold py-3 rounded-xl flex items-center justify-center gap-2"><Share2 size={18} /> Share my code</button>
        <a href="#earnings" className="bg-white border border-slate-200 text-[#1e3a5f] font-bold py-3 rounded-xl flex items-center justify-center gap-2"><TrendingUp size={18} /> See what I’ve earned</a>
        <button onClick={() => setShowWithdraw(true)} className="bg-white border border-slate-200 text-[#1e3a5f] font-bold py-3 rounded-xl flex items-center justify-center gap-2"><Wallet size={18} /> Withdraw</button>
      </div>

      {/* Code + share + QR */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="text-xs text-slate-400">Your discount code</div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <span className="text-2xl font-black tracking-wider text-[#1e3a5f]">{code}</span>
          <button onClick={copyCode} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm flex items-center gap-1">{copied ? <Check size={14} /> : <Copy size={14} />} Copy</button>
          <button onClick={share} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm flex items-center gap-1"><Share2 size={14} /> Share link</button>
          <button onClick={() => setShowQr((v) => !v)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm flex items-center gap-1"><QrCode size={14} /> QR code</button>
        </div>
        <div className="text-xs text-slate-400 mt-2 break-all">{shareUrl}</div>
        {showQr && code && (
          <div className="mt-4 flex flex-col items-center gap-2">
            <img src={`/api/influencer/qr/${encodeURIComponent(code)}`} alt="Your QR code" width={220} height={220} className="rounded-xl border border-slate-100" />
            <a href={`/api/influencer/qr/${encodeURIComponent(code)}`} download={`ayrshirehub-${code}.png`} className="text-sm text-[#1e3a5f] font-semibold flex items-center gap-1"><Download size={14} /> Download QR</a>
          </div>
        )}
        <div className="mt-3 flex gap-4 text-sm text-slate-500">
          <span className="flex items-center gap-1"><Users size={14} /> {data.referredCount} referred</span>
          <span className="flex items-center gap-1"><TrendingUp size={14} /> {data.repeatCustomerCount} ordered again</span>
        </div>
      </div>

      {/* Earnings history */}
      <div id="earnings" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h3 className="text-lg font-bold text-slate-800 mb-3">Earnings history</h3>
        {earnings && earnings.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">No earnings yet — share your code to get started.</div>}
        {earnings && earnings.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100"><th className="py-2 pr-3">Date</th><th className="pr-3">Referral</th><th className="pr-3">Stage</th><th className="pr-3">Amount</th><th>Status</th></tr></thead>
              <tbody>
                {earnings.map((e) => (
                  <tr key={e.id} className="border-b border-slate-50">
                    <td className="py-2 pr-3 text-slate-500">{fmtDate(e.date)}</td>
                    <td className="pr-3 font-mono text-xs text-slate-500">{e.referralRef}</td>
                    <td className="pr-3 text-slate-700">{e.stage}</td>
                    <td className={`pr-3 font-semibold ${e.amountPence < 0 ? "text-rose-600" : "text-[#1e3a5f]"}`}>{pence(e.amountPence)}</td>
                    <td><StatusPill status={e.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Withdrawals list */}
      {withdrawals && withdrawals.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="text-lg font-bold text-slate-800 mb-3">Withdrawals</h3>
          <div className="space-y-2">
            {withdrawals.map((w) => (
              <div key={w.id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2">
                <span className="text-slate-500">{fmtDate(w.requestedAt)}</span>
                <span className="font-semibold text-[#1e3a5f]">{pence(w.amountPence)}</span>
                <StatusPill status={w.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {showWithdraw && <WithdrawModal terms={terms} available={b.availableToWithdrawPence} hasBank={!!me.hasBankDetails} onClose={() => setShowWithdraw(false)} onDone={() => { setShowWithdraw(false); void load(); }} />}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    available: "bg-emerald-50 text-emerald-700", pending: "bg-amber-50 text-amber-700", reversed: "bg-rose-50 text-rose-700",
    paid: "bg-emerald-50 text-emerald-700", processing: "bg-blue-50 text-blue-700", rejected: "bg-rose-50 text-rose-700", failed: "bg-rose-50 text-rose-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status] ?? "bg-slate-100 text-slate-600"}`}>{status}</span>;
}

function WithdrawModal({ terms, available, hasBank, onClose, onDone }: { terms: Terms; available: number; hasBank: boolean; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const [amount, setAmount] = useState((available / 100).toFixed(2));
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [needBank, setNeedBank] = useState(!hasBank);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      if (needBank) {
        const r = await fetch("/api/influencer/bank-details", { method: "PUT", headers: authHeaders(), body: JSON.stringify({ accountName, accountNumber, sortCode }) });
        if (!r.ok) { const e = await r.json().catch(() => ({})); toast({ title: e.error || "Invalid bank details", variant: "destructive" }); setBusy(false); return; }
      }
      const amountPence = Math.round(Number(amount) * 100);
      const res = await fetch("/api/influencer/withdrawals", { method: "POST", headers: authHeaders(), body: JSON.stringify({ amountPence }) });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { toast({ title: "Withdrawal requested", description: "You’ll be paid by bank transfer." }); onDone(); }
      else toast({ title: data.error || "Could not request", variant: "destructive" });
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-800">Withdraw earnings</h3>
        <p className="text-sm text-slate-500 mt-1">Available: <strong>{pence(available)}</strong> · Minimum {pence(terms.minWithdrawalPence)} · Paid by bank transfer (usually a few working days).</p>
        <label className="block text-sm text-slate-600 mt-4">Amount (£)
          <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-lg border border-slate-200" /></label>
        {needBank ? (
          <div className="space-y-2 mt-3">
            <div className="text-sm font-semibold text-slate-700">Payout bank details</div>
            <input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="Account holder name" className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
            <input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="Account number" className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
            <input value={sortCode} onChange={(e) => setSortCode(e.target.value)} placeholder="Sort code (6 digits)" className="w-full px-3 py-2.5 rounded-lg border border-slate-200" />
          </div>
        ) : (
          <button onClick={() => setNeedBank(true)} className="text-sm text-[#1e3a5f] font-semibold mt-2">Change payout bank details</button>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 font-semibold text-slate-600">Cancel</button>
          <button onClick={submit} disabled={busy} className="flex-1 py-3 rounded-xl bg-[#f0a500] text-[#1e3a5f] font-bold flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={18} className="animate-spin" /> : null} Request</button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Influencer() {
  const { user, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [terms, setTerms] = useState<Terms>(DEFAULT_TERMS);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    setLoading(true);
    const [t, m] = await Promise.all([
      fetch("/api/influencer/terms").then((r) => r.ok ? r.json() : DEFAULT_TERMS).catch(() => DEFAULT_TERMS),
      user ? fetch("/api/influencer/me", { headers: authHeaders() }).then((r) => r.ok ? r.json() : { isInfluencer: false, status: null }).catch(() => ({ isInfluencer: false, status: null })) : Promise.resolve({ isInfluencer: false, status: null } as Me),
    ]);
    setTerms(t); setMe(m); setLoading(false);
  }, [user]);
  useEffect(() => { if (!isLoading) void loadMe(); }, [isLoading, loadMe]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      {(isLoading || loading) && <div className="py-20 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>}

      {!isLoading && !loading && (
        <>
          {!user && (
            <>
              <OfferExplainer terms={terms} />
              <Calculator terms={terms} />
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 text-center">
                <p className="text-slate-600">Log in to apply and start earning.</p>
                <button onClick={() => setLocation("/login")} className="mt-3 bg-[#1e3a5f] text-white font-bold px-5 py-2.5 rounded-xl">Log in</button>
              </div>
            </>
          )}

          {user && me && !me.isInfluencer && (
            <>
              <OfferExplainer terms={terms} />
              <Calculator terms={terms} />
              <ApplyForm onApplied={loadMe} />
            </>
          )}

          {user && me?.isInfluencer && me.status === "pending" && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center">
              <Clock className="mx-auto text-[#f0a500]" size={36} />
              <h2 className="text-xl font-bold text-slate-800 mt-3">Application under review</h2>
              <p className="text-slate-500 mt-1">We’ll let you know once you’re approved and your code is ready.</p>
            </div>
          )}

          {user && me?.isInfluencer && me.status === "rejected" && (
            <DeclinedView reason={me.rejectionReason} onApplied={loadMe} />
          )}

          {user && me?.isInfluencer && (me.status === "active" || me.status === "paused") && <Dashboard me={me} terms={terms} />}
        </>
      )}
    </div>
  );
}
